import { Marked, type RendererObject, type Tokens } from 'marked';

/** Turning a converted document into something safe to show.
 *
 *  The content is written by whoever built the sample, so the render surface
 *  must not be able to make a request. Two layers hold that: external images
 *  are rewritten to inert text here, and the result is served into an iframe
 *  with an empty `sandbox` and `default-src 'none'`.
 *
 *  Both matter. A document carrying `![](https://attacker/beacon.png)` would
 *  otherwise beacon the moment the pane opened. The sandbox makes a mistake
 *  here harmless; the rewriting makes the intent visible. */

/** Defang a URL for display: no scheme a browser will act on, no bare host. */
export function defang(url: string): string {
  return url
    .replace(/^http:/i, 'hxxp:')
    .replace(/^https:/i, 'hxxps:')
    .replace(/^ftp:/i, 'fxp:')
    .replace(/\./g, '[.]');
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** A renderer that cannot emit anything which loads or navigates. Images become
 *  a placeholder naming their source, links their text plus a defanged target -
 *  neither an element a browser will act on. */
function inertRenderer(): RendererObject {
  return {
    image(token: Tokens.Image): string {
      const alt = escapeHtml(token.text || 'image');
      const src = token.href ?? '';
      // An embedded image has no URL; an external one is a beacon until
      // proven otherwise, so it is named rather than loaded.
      const origin = src ? ` &middot; <span class="u">${escapeHtml(defang(src))}</span>` : '';
      return `<span class="img">[image: ${alt}${origin}]</span>`;
    },
    link(token: Tokens.Link): string {
      const text = escapeHtml(token.text || '');
      const href = token.href ?? '';
      if (!href) return text;
      return `${text} <span class="u">&lt;${escapeHtml(defang(href))}&gt;</span>`;
    },
  };
}

/** Convert the document's Markdown into the HTML the preview iframe shows. */
function renderInertHtml(markdown: string): string {
  const instance = new Marked({ gfm: true, breaks: false, async: false });
  instance.use({ renderer: inertRenderer() });
  return instance.parse(markdown) as string;
}

/** The stylesheet the preview document carries, inlined because the iframe's
 *  policy denies every external source including this one. */
function previewStyles(): string {
  return `
    :root { color-scheme: light dark; }
    body {
      margin: 0; padding: 1rem 1.15rem;
      font: 13px/1.6 ui-sans-serif, system-ui, sans-serif;
      color: var(--fg); background: var(--bg);
      word-wrap: break-word; overflow-wrap: anywhere;
    }
    h1, h2, h3, h4 { margin: 1.1em 0 .45em; line-height: 1.25; font-weight: 600; }
    h1 { font-size: 1.35em; } h2 { font-size: 1.2em; }
    h3 { font-size: 1.08em; } h4 { font-size: 1em; }
    p, ul, ol, blockquote, table, pre { margin: 0 0 .8em; }
    ul, ol { padding-left: 1.4em; }
    blockquote { border-left: 2px solid var(--rule); padding-left: .8em; color: var(--muted); }
    code, pre { font-family: ui-monospace, SFMono-Regular, monospace; font-size: .92em; }
    pre { background: var(--sunken); padding: .7em .85em; border-radius: 4px; overflow-x: auto; }
    code { background: var(--sunken); padding: .1em .3em; border-radius: 3px; }
    pre code { background: none; padding: 0; }
    table { border-collapse: collapse; width: 100%; display: block; overflow-x: auto; }
    th, td { border: 1px solid var(--rule); padding: .35em .6em; text-align: left; }
    th { background: var(--sunken); font-weight: 600; }
    hr { border: 0; border-top: 1px solid var(--rule); margin: 1.2em 0; }
    .u { color: var(--muted); font-family: ui-monospace, monospace; font-size: .9em; }
    .img { color: var(--muted); font-style: italic; }
  `;
}

/** Theme tokens the preview document inlines. The iframe inherits nothing from
 *  the dashboard, so the host resolves them and passes the values in. */
export interface PreviewTheme {
  fg: string;
  bg: string;
  muted: string;
  rule: string;
  sunken: string;
}

/** Read the dashboard's palette for the theme currently applied. The tokens are
 *  RGB channel triplets, wrapped back into colours because the preview document
 *  has no Tailwind to compose them. */
export function readPreviewTheme(root: HTMLElement = document.documentElement): PreviewTheme {
  const style = getComputedStyle(root);
  const token = (name: string, fallback: string): string => {
    const channels = style.getPropertyValue(name).trim();
    return channels ? `rgb(${channels})` : fallback;
  };
  return {
    fg: token('--ink-100', '#e8eefb'),
    bg: token('--ink-900', '#162135'),
    muted: token('--ink-400', '#8b9cc0'),
    rule: token('--ink-800', '#22314c'),
    sunken: token('--ink-950', '#0d131c'),
  };
}

/** Build the complete document for the preview iframe's `srcdoc`.
 *
 *  `default-src 'none'` denies every source and every form of fetch;
 *  `style-src 'unsafe-inline'` re-permits only the stylesheet inlined above.
 *  With an empty `sandbox` on the iframe, the pane cannot reach the network or
 *  the page hosting it. */
export function buildPreviewDocument(markdown: string, theme: PreviewTheme): string {
  const vars = `--fg:${theme.fg};--bg:${theme.bg};--muted:${theme.muted};--rule:${theme.rule};--sunken:${theme.sunken};`;
  return [
    '<!doctype html><html><head><meta charset="utf-8">',
    `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'">`,
    `<style>:root{${vars}}${previewStyles()}</style>`,
    '</head><body>',
    renderInertHtml(markdown),
    '</body></html>',
  ].join('');
}
